import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from './toast';

const COPIED_RESET_MS = 2000;

interface SharePayload {
    title: string;
    url: string;
}

/**
 * useShareOrCopy — shared share-with-fallback hook for any "share this thing" UI.
 *
 * Returns:
 *   - status: 'idle' | 'copied' — drives any local "Copied!" label flip the
 *     consumer wants to show. Resets to 'idle' 2 seconds after a successful
 *     clipboard fallback. Stays 'idle' for the native Web Share path
 *     (the OS share sheet is its own feedback).
 *   - share(payload): imperative async fn the consumer calls from a click
 *     handler with { title, url }.
 *
 * Behaviour (extracted from the original inline blog ShareButton):
 *   - If navigator.share is available AND navigator.canShare accepts the
 *     payload, opens the OS-native share sheet. AbortError (user dismissed)
 *     returns silently — no clipboard fallback for an explicit cancel.
 *     Other share errors fall through to clipboard.
 *   - Clipboard fallback: navigator.clipboard.writeText(url), sets
 *     status='copied', fires toast.success('Link copied to clipboard'),
 *     schedules a 2000ms reset to 'idle'. Rapid re-clicks reset the timer.
 *   - Clipboard failures are swallowed silently (no toast / error UX —
 *     production HTTPS makes this unreachable in practice).
 *
 * Accessibility:
 *   - aria-live announce comes from the central Toast component (mounted by
 *     ToastProvider in App.tsx). Consumers do NOT need their own sr-only
 *     status span — the toast handles it for every consumer.
 */
export function useShareOrCopy() {
    const [status, setStatus] = useState<'idle' | 'copied'>('idle');
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
        };
    }, []);

    const share = useCallback(async (payload: SharePayload) => {
        const canUseNativeShare =
            typeof navigator !== 'undefined' &&
            typeof navigator.share === 'function' &&
            (!navigator.canShare || navigator.canShare(payload));

        if (canUseNativeShare) {
            try {
                await navigator.share(payload);
                return;
            } catch (err) {
                // User dismissed the share sheet — return silently, no fallback.
                if (err instanceof Error && err.name === 'AbortError') return;
                // Any other share error → fall through to clipboard.
            }
        }

        try {
            await navigator.clipboard.writeText(payload.url);
            if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
            setStatus('copied');
            resetTimerRef.current = setTimeout(() => setStatus('idle'), COPIED_RESET_MS);
            toast.success('Link copied to clipboard');
        } catch {
            // Silent fail per operator decision. Production HTTPS makes this
            // unreachable in practice.
        }
    }, []);

    return { status, share };
}
